package com.exura.pages.planDistribution

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.legacyplans.domain.usecase.DistributeDetailedPlanViaReceiverIdUseCase
import com.exura.features.legacyplans.domain.usecase.SaveDetailedPlanUseCase
import com.exura.features.planDistribution.distLocalization
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.plan.TreatmentPlan
import com.exura.features.plandefinition.usecase.GetTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.GetVersionContentUseCase
import com.exura.navigation.Route
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

enum class DistributionOption {
    PATIENT_CODE,
    GENERATE_QR,
    PRE_PRINTED,
    PLANS_CODE;

}

sealed interface PlanDistributionEvent {
    data object LoadPlan : PlanDistributionEvent
    data object GoBack : PlanDistributionEvent
    data class SelectDistributionOption(val option: DistributionOption) : PlanDistributionEvent
    data class UpdatePatientCode(val code: String) : PlanDistributionEvent
    data class UpdateDateTime(val dateTime: LocalDateTime?) : PlanDistributionEvent
    data class DistributePlan(
        val onSuccess: (String) -> Unit,
        val onFailure: (String) -> Unit
    ) : PlanDistributionEvent
    data object ClearError : PlanDistributionEvent
}

data class PlanDistributionUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val planVersion: Version<BasePlan, PlanDefinitionMetadata>? = null,
    val planContent: BasePlan? = null,
    val selectedDistributionOption: DistributionOption? = null,
    val patientCode: String = "",
    val selectedDateTime: LocalDateTime? = null,
    val isSendingPlan: Boolean = false,
    val distributionError: String = ""
)

class PlanDistributionController(
    private val planId: String,
    private val scope: CoroutineScope,
    private val context: PageContext,
    private val getTreatmentPlanUseCase: GetTreatmentPlanUseCase,
    private val getVersionContentUseCase: GetVersionContentUseCase,
    private val saveDetailedPlanUseCase: SaveDetailedPlanUseCase,
    private val distributeDetailedPlanViaReceiverIdUseCase: DistributeDetailedPlanViaReceiverIdUseCase,
) {
    var uiState by mutableStateOf(PlanDistributionUiState())
        private set

    init {
        loadPlan()
    }

    fun loadPlan() {
        scope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            getTreatmentPlanUseCase(planId)
                .onSuccess { version ->
                    val content = getVersionContentUseCase(version)
                    val initialDateTime = if (content is TreatmentPlan) {
                        Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
                    } else null
                    
                    uiState = uiState.copy(
                        isLoading = false,
                        planVersion = version,
                        planContent = content,
                        selectedDateTime = initialDateTime
                    )
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
        }
    }

    fun onSelectDistributionOption(option: DistributionOption) {
        val newOption = if (uiState.selectedDistributionOption == option) null else option
        uiState = uiState.copy(selectedDistributionOption = newOption)
    }

    fun onUpdatePatientCode(code: String) {
        uiState = uiState.copy(
            patientCode = "#" + code.substringAfterLast("#"),
            distributionError = ""
        )
    }

    fun onUpdateDateTime(dateTime: LocalDateTime?) {
        uiState = uiState.copy(
            selectedDateTime = dateTime,
            distributionError = ""
        )
    }

    fun onClearError() {
        uiState = uiState.copy(distributionError = "")
    }

    fun onDistributePlan(onSuccess: (String) -> Unit, onFailure: (String) -> Unit) {
        val plan = uiState.planContent ?: return
        val dateTime = uiState.selectedDateTime
        val patientCode = uiState.patientCode

        when {
            dateTime == null -> {
                uiState = uiState.copy(distributionError = distLocalization.current.patientCode.dateTimeRequired)
                return
            }
            patientCode.isBlank() -> {
                uiState = uiState.copy(distributionError = distLocalization.current.patientCode.patientCodeCannotBeEmpty)
                return
            }
        }

        scope.launch {
            uiState = uiState.copy(isSendingPlan = true)
            
            saveDetailedPlanUseCase(plan, dateTime)
                .onSuccess { savedPlanId ->
                    distributeDetailedPlanViaReceiverIdUseCase(
                        receiverId = patientCode,
                        detailedPlanId = savedPlanId,
                    )
                        .onSuccess {
                            // Reset form
                            val newDateTime = if (plan is TreatmentPlan) {
                                Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
                            } else null
                            
                            uiState = uiState.copy(
                                isSendingPlan = false,
                                patientCode = "",
                                selectedDateTime = newDateTime,
                                distributionError = ""
                            )
                            onSuccess(distLocalization.current.distributedSuccessfully)
                        }
                        .onFailure { error ->
                            uiState = uiState.copy(isSendingPlan = false)
                            onFailure(error.message ?: distLocalization.current.failedToDistributePlan)
                        }
                }
                .onFailure { error ->
                    uiState = uiState.copy(isSendingPlan = false)
                    onFailure(error.message ?: distLocalization.current.failedToSavePlan)
                }
        }
    }

    fun onBackClicked() {
        context.router.navigateTo(Route.DASHBOARD)
    }

    fun handleEvent(event: PlanDistributionEvent) {
        when (event) {
            is PlanDistributionEvent.LoadPlan -> loadPlan()
            is PlanDistributionEvent.GoBack -> onBackClicked()
            is PlanDistributionEvent.SelectDistributionOption -> onSelectDistributionOption(event.option)
            is PlanDistributionEvent.UpdatePatientCode -> onUpdatePatientCode(event.code)
            is PlanDistributionEvent.UpdateDateTime -> onUpdateDateTime(event.dateTime)
            is PlanDistributionEvent.DistributePlan -> onDistributePlan(event.onSuccess, event.onFailure)
            is PlanDistributionEvent.ClearError -> onClearError()
        }
    }
}

@Composable
fun rememberPlanDistributionController(
    scope: CoroutineScope,
    context: PageContext,
    planId: String,
): PlanDistributionController {
    val getTreatmentPlan = injectAndRemember<GetTreatmentPlanUseCase>()
    val getVersionContent = injectAndRemember<GetVersionContentUseCase>()
    val saveDetailedPlan = injectAndRemember<SaveDetailedPlanUseCase>()
    val distributeDetailedPlan = injectAndRemember<DistributeDetailedPlanViaReceiverIdUseCase>()

    return remember(scope, context, planId) {
        PlanDistributionController(
            planId = planId,
            scope = scope,
            context = context,
            getTreatmentPlanUseCase = getTreatmentPlan,
            getVersionContentUseCase = getVersionContent,
            saveDetailedPlanUseCase = saveDetailedPlan,
            distributeDetailedPlanViaReceiverIdUseCase = distributeDetailedPlan,
        )
    }
}
